import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import {
    type MutationOrderCancelArgs,
    type OrderItemDetails,
    OrderStatus,
    OrderType,
    Side,
} from '@pkg/graphql/codegen/graphql';
import { useAppSelector, useQueryInvalidation } from '@pkg/hooks';

import { callCancelOrder, callGetUserOrders } from '@app/client/resources/marketplace';
import { useAuthTokens } from '@app/store/zustand';

interface FetchOrdersArgs {
    disabled?: boolean;
    useCurrentUser?: boolean;
}

type Offers = { incoming: OrderItemDetails[]; outgoing: OrderItemDetails[] };

export const CURRENT_USER_ORDERS_KEY = 'current-user-orders';

function splitOrderTypes(orders: OrderItemDetails[]) {
    const listings = orders.filter(
        (order) => order.type === OrderType.Limit && order.side === Side.Ask && order.status !== OrderStatus.Failed,
    );
    const offers = orders.reduce(
        (acc: Offers, curr: OrderItemDetails) => {
            if (curr.type === OrderType.Limit && curr.side === Side.Bid && curr.status !== OrderStatus.Failed) {
                if (!curr.oppositeAddress) {
                    acc.outgoing.push(curr);
                } else {
                    acc.incoming.push(curr);
                }
            }
            return acc;
        },
        { incoming: [], outgoing: [] },
    );

    return { listings, incomingOffers: offers.incoming, outgoingOffers: offers.outgoing };
}

export function useFetchOrders(args?: FetchOrdersArgs) {
    const { isAuthenticated } = useAuthTokens();
    const { disabled = false, useCurrentUser = false } = args || {};
    const pageUserId = useAppSelector((s) => s.yatPage.user_id);

    const userFilter = useCurrentUser ? { current: true } : { id: pageUserId };
    const missingPageId = !useCurrentUser ? !pageUserId?.length : false;
    const enabled = !disabled && !missingPageId && isAuthenticated;

    const queryKey = [CURRENT_USER_ORDERS_KEY, pageUserId, useCurrentUser];
    return useQuery({
        queryKey,
        queryFn: ({ signal }) =>
            callGetUserOrders({
                settings: { signal },
                variables: {
                    userFilter,
                },
            }),
        placeholderData: keepPreviousData,
        enabled,
        staleTime: Infinity,
        retry: false,
        select: (data) => {
            if (data?.orders) {
                const orders = data.orders.filter(
                    (order) => order.status !== OrderStatus.Failed && order.status !== OrderStatus.New,
                );
                const { listings, incomingOffers, outgoingOffers } = splitOrderTypes(orders);
                return { orders: orders, listings, incomingOffers, outgoingOffers };
            } else {
                return { orders: [] as OrderItemDetails[], listings: [], incomingOffers: [], outgoingOffers: [] };
            }
        },
    });
}

export function useCancelOrder() {
    const invalidateOrders = useQueryInvalidation([CURRENT_USER_ORDERS_KEY]);
    return useMutation({
        mutationFn: (variables: MutationOrderCancelArgs) => callCancelOrder({ variables }),
        onSuccess: invalidateOrders,
    });
}
